import { useQuery } from '@tanstack/react-query'
import { useAuth } from './auth'
import { supabase } from './supabase'

export { adminGate, type AdminGate } from './adminGate.ts'

/**
 * Whether the signed-in user is an admin — `true`, `false`, or `undefined`
 * while the answer is not in yet. Read from the `app_admins` table (a user may
 * select their own row; U0.2), not decoded from the JWT: one source of truth,
 * and the same table `is_admin()` reads when Postgres decides a write.
 *
 * Keyed by user id, so a second person on the same device never reads the
 * first one's answer out of the persisted cache. `undefined` is not `false`:
 * every caller passes the result through `adminGate`, which hides on both.
 *
 * A failed read leaves `data` undefined, which hides the controls — the safe
 * side to fail on for a gate that is only cosmetic.
 */
export function useIsAdmin(): boolean | undefined {
  const { session } = useAuth()
  const uid = session?.user?.id
  const q = useQuery({
    queryKey: ['is_admin', uid],
    enabled: !!uid,
    staleTime: 60 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_admins').select('user_id').eq('user_id', uid!).maybeSingle()
      if (error) throw error
      return data !== null
    },
  })
  return uid ? q.data : undefined
}
